import React, { useState, useRef } from 'react';
import { ArrowLeft, Upload } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const EditProfile = ({ user, onBack }) => {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  
  // Obtener la foto actual del usuario (soporta múltiples campos)
  const initialPhoto = user.profile_picture_url || user.avatarUrl || user.avatar_url || user.photo_url || null;
  const [profilePicture, setProfilePicture] = useState(initialPhoto);
  
  const [formData, setFormData] = useState({
    fullName: user.name || '',
    phone: user.phone || '',
    timezone: user.timezone || '(GMT -05:00) Eastern Time (US & Canada)',
    language: user.language || 'English',
    location: user.location || '',
    headline: user.headline || '',
    website: user.website || '',
    linkedinUrl: user.linkedinUrl || '',
    profession: user.profession || '',
    industry: user.industry || 'Software and IT Services',
    country: user.country || 'United States of America',
    company: user.company || '',
    city: user.city || '',
    preventMessages: user.preventMessages || false,
    showInDirectory: user.showInDirectory !== false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona un archivo de imagen válido');
      return;
    }

    // Validar tamaño (5MB máximo)
    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen debe ser menor a 5MB');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Subir foto usando el endpoint de admin
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/users/${user.id}/profile-picture`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Actualizar la vista previa con la nueva URL
      const newPhotoUrl = response.data.profile_picture_url;
      setProfilePicture(newPhotoUrl);
      
      // Actualizar el objeto user si es posible
      if (user) {
        user.profile_picture_url = newPhotoUrl;
        user.avatarUrl = newPhotoUrl;
      }
      
      alert('Foto de perfil actualizada exitosamente. Recarga la página para ver los cambios en toda la aplicación.');
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert(error.response?.data?.detail || 'Error al subir la foto. Por favor intenta de nuevo.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await axios.patch(
        `${BACKEND_URL}/api/auth/me`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      alert('✅ Perfil actualizado correctamente');
      
      // Recargar la página para que se actualice el contexto
      window.location.reload();
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('❌ Error al actualizar el perfil: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="bg-gray-900 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Profile</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold mb-8">Profile</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Photo */}
          <div>
            <h3 className="text-sm text-gray-400 mb-1">Profile photo</h3>
            <p className="text-xs text-gray-500 mb-3">Recommended size: 300 x 300. Max: 5MB</p>
            
            {/* Preview de la foto actual */}
            <div className="mb-4">
              {profilePicture ? (
                <div className="relative inline-block">
                  <img
                    src={profilePicture}
                    alt="Profile"
                    className="w-32 h-32 rounded-full object-cover border-2 border-teal-500/30"
                  />
                  <div className="absolute bottom-0 right-0 bg-teal-500 rounded-full p-2">
                    <Upload size={16} className="text-white" />
                  </div>
                </div>
              ) : (
                <div className="w-32 h-32 rounded-full bg-gray-800 border-2 border-white/20 flex items-center justify-center">
                  <Upload size={32} className="text-gray-500" />
                </div>
              )}
            </div>
            
            {/* Input oculto */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handlePhotoChange}
              className="hidden"
            />
            
            {/* Botón de upload */}
            <button
              type="button"
              onClick={handlePhotoClick}
              disabled={uploading}
              className="px-6 py-2 border border-white/20 rounded-full hover:bg-white/5 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <Upload size={16} />
              <span>{uploading ? 'Subiendo...' : (profilePicture ? 'Cambiar foto' : 'Subir foto')}</span>
            </button>
            {profilePicture && (
              <p className="text-xs text-gray-500 mt-2">
                ✓ Foto guardada. Los cambios se reflejarán al recargar la página.
              </p>
            )}
          </div>

          {/* Full Name */}
          <div>
            <label className="block text-sm mb-2">
              Full name<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg focus:outline-none focus:border-teal-500 transition-colors"
            />
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm mb-2">
              Teléfono <span className="text-gray-400 text-xs">(incluye código de área)</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+52 55 1234 5678"
              className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg focus:outline-none focus:border-teal-500 transition-colors"
            />
            <p className="text-xs text-gray-500 mt-1">
              💡 Necesario para ver tu información personalizada de beca
            </p>
          </div>

          {/* Timezone */}
          <div>
            <label className="block text-sm mb-2">
              Timezone<span className="text-red-500">*</span>
            </label>
            <select
              name="timezone"
              value={formData.timezone}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg focus:outline-none focus:border-teal-500 transition-colors appearance-none"
            >
              <option>(GMT -05:00) Eastern Time (US & Canada)</option>
              <option>(GMT -06:00) Central Time (US & Canada)</option>
              <option>(GMT -07:00) Mountain Time (US & Canada)</option>
              <option>(GMT -08:00) Pacific Time (US & Canada)</option>
              <option>(GMT +00:00) UTC</option>
            </select>
          </div>

          {/* Language */}
          <div>
            <label className="block text-sm mb-2">
              Language<span className="text-red-500">*</span>
            </label>
            <select
              name="language"
              value={formData.language}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg focus:outline-none focus:border-teal-500 transition-colors appearance-none"
            >
              <option>English</option>
              <option>Español</option>
              <option>Português</option>
            </select>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm mb-2 flex items-center space-x-2">
              <span>Location</span>
              <span className="text-gray-500 text-xs">ⓘ</span>
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Search location"
              className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg focus:outline-none focus:border-teal-500 transition-colors"
            />
            <div className="mt-3 p-4 bg-gray-800/50 border border-white/10 rounded-lg flex items-start space-x-3">
              <span className="text-gray-400 text-sm">ⓘ</span>
              <p className="text-xs text-gray-400">
                Update your location for enhanced features. We've updated our location system to provide better community experiences. Sharing your location helps us personalize your experience and gradually introduce new, relevant features.
              </p>
            </div>
          </div>

          {/* Headline */}
          <div>
            <label className="block text-sm mb-2 flex items-center space-x-2">
              <span>Headline</span>
              <span className="text-gray-500 text-xs">ⓘ</span>
            </label>
            <input
              type="text"
              name="headline"
              value={formData.headline}
              onChange={handleChange}
              placeholder="Add your headline"
              className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg focus:outline-none focus:border-teal-500 transition-colors"
            />
          </div>

          {/* Website */}
          <div>
            <label className="block text-sm mb-2 flex items-center space-x-2">
              <span>Website</span>
              <span className="text-gray-500 text-xs">ⓘ</span>
            </label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              placeholder="https://www.example.com"
              className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg focus:outline-none focus:border-teal-500 transition-colors"
            />
          </div>

          {/* LinkedIn URL */}
          <div>
            <label className="block text-sm mb-2 flex items-center space-x-2">
              <span>Linkedin URL</span>
              <span className="text-gray-500 text-xs">ⓘ</span>
            </label>
            <input
              type="url"
              name="linkedinUrl"
              value={formData.linkedinUrl}
              onChange={handleChange}
              placeholder="https://www.linkedin.com/in/username"
              className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg focus:outline-none focus:border-teal-500 transition-colors"
            />
          </div>

          {/* Profession */}
          <div>
            <label className="block text-sm mb-2 flex items-center space-x-2">
              <span>Profession</span>
              <span className="text-gray-500 text-xs">ⓘ</span>
            </label>
            <input
              type="text"
              name="profession"
              value={formData.profession}
              onChange={handleChange}
              placeholder="Your profession"
              className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg focus:outline-none focus:border-teal-500 transition-colors"
            />
          </div>

          {/* Company */}
          <div>
            <label className="block text-sm mb-2 flex items-center space-x-2">
              <span>Company</span>
              <span className="text-red-500">*</span>
              <span className="text-gray-500 text-xs">ⓘ</span>
            </label>
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Add your company"
              className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg focus:outline-none focus:border-teal-500 transition-colors"
            />
          </div>

          {/* City */}
          <div>
            <label className="block text-sm mb-2">
              City<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="Your city"
              className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg focus:outline-none focus:border-teal-500 transition-colors"
            />
          </div>

          {/* Industry */}
          <div>
            <label className="block text-sm mb-2 flex items-center space-x-2">
              <span>Industry</span>
              <span className="text-gray-500 text-xs">ⓘ</span>
            </label>
            <select
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg focus:outline-none focus:border-teal-500 transition-colors appearance-none"
            >
              <option>Software and IT Services</option>
              <option>Finance and Banking</option>
              <option>Healthcare</option>
              <option>Education</option>
              <option>Manufacturing</option>
              <option>Retail</option>
              <option>Other</option>
            </select>
          </div>

          {/* Country */}
          <div>
            <label className="block text-sm mb-2 flex items-center space-x-2">
              <span>Country</span>
              <span className="text-gray-500 text-xs">ⓘ</span>
            </label>
            <select
              name="country"
              value={formData.country}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg focus:outline-none focus:border-teal-500 transition-colors appearance-none"
            >
              <option>United States of America</option>
              <option>Canada</option>
              <option>United Kingdom</option>
              <option>Mexico</option>
              <option>Spain</option>
              <option>Argentina</option>
              <option>Colombia</option>
              <option>Other</option>
            </select>
          </div>

          {/* Permissions */}
          <div className="pt-6">
            <h3 className="text-xl font-bold mb-4">Permissions</h3>
            
            <div className="space-y-4">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="preventMessages"
                  checked={formData.preventMessages}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-white/20 bg-gray-800 text-teal-500 focus:ring-teal-500 focus:ring-offset-0"
                />
                <span className="text-sm">
                  Prevent members from messaging me{' '}
                  <span className="text-gray-500 text-xs">ⓘ</span>
                </span>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="showInDirectory"
                  checked={formData.showInDirectory}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-white/20 bg-gray-800 text-teal-500 focus:ring-teal-500 focus:ring-offset-0"
                />
                <span className="text-sm">
                  Show my profile in the member directory{' '}
                  <span className="text-gray-500 text-xs">ⓘ</span>
                </span>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-4 pt-8">
            <button
              type="button"
              onClick={onBack}
              className="px-8 py-3 border border-white/20 rounded-full hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-8 py-3 bg-white text-black font-semibold rounded-full hover:bg-gray-200 transition-colors"
            >
              Save changes
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default EditProfile;
